import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Field Launcher Tab
 * --------------------------------------------------------------------------
 * Dashboard tile where Shannon can:
 *   1. ⚡ One-button launch Field Mode for herself on any project
 *   2. 🔗 Generate magic crew links per project (no Google login required)
 *   3. 📋 See active sessions + revoke crew links
 */

const FieldLauncherTab = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [crewLinks, setCrewLinks] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [crewRole, setCrewRole] = useState("field_crew");
  const [expiresIn, setExpiresIn] = useState(7);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const showToast = (msg, kind = "success") => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 3500);
  };

  const loadAll = useCallback(async () => {
    try {
      const [pr, lr, sr] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/projects`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/admin/field/crew-links`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/admin/field/sessions?status=active`, { credentials: "include" }),
      ]);
      if (pr.ok) {
        const d = await pr.json();
        setProjects(Array.isArray(d) ? d : (d.projects || []));
      }
      if (lr.ok) {
        const d = await lr.json();
        setCrewLinks(d.links || []);
      }
      if (sr.ok) {
        const d = await sr.json();
        setSessions(d.sessions || []);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const launchSelf = () => {
    if (!selectedProject) { showToast("Pick a project first", "error"); return; }
    navigate(`/field/${selectedProject}`);
  };

  // Clipboard write with fallback. navigator.clipboard is blocked inside
  // the Emergent preview iframe on mobile Chrome, so the promise silently
  // rejects and the previous code lied to the user with a "copied" toast.
  // We fall back to a hidden textarea + execCommand('copy'). If that also
  // fails (rare), we pop a modal-style prompt exposing the URL so the user
  // can long-press → copy manually.
  const [copyModalUrl, setCopyModalUrl] = useState("");
  const writeToClipboard = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch { /* fall through to execCommand */ }
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.top = "-1000px";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      ta.setSelectionRange(0, text.length);
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (ok) return true;
    } catch { /* fall through */ }
    return false;
  };

  const generateCrewLink = async () => {
    if (!selectedProject) { showToast("Pick a project first", "error"); return; }
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/field/crew-link`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ project_id: selectedProject, expires_in_days: expiresIn, crew_role: crewRole }),
      });
      const d = await r.json();
      if (r.ok) {
        const fullUrl = `${window.location.origin}/crew/${d.token}`;
        const ok = await writeToClipboard(fullUrl);
        if (ok) {
          showToast("✓ Link generated + copied to clipboard");
        } else {
          setCopyModalUrl(fullUrl);
          showToast("Link generated — tap the URL below and long-press to copy", "error");
        }
        await loadAll();
      } else {
        showToast(d.detail || "Could not generate link", "error");
      }
    } catch {
      showToast("Could not generate link", "error");
    }
  };

  const copyLink = async (token) => {
    const fullUrl = `${window.location.origin}/crew/${token}`;
    const ok = await writeToClipboard(fullUrl);
    if (ok) {
      showToast("✓ Link copied to clipboard");
    } else {
      setCopyModalUrl(fullUrl);
      showToast("Long-press the URL below to copy", "error");
    }
  };

  // Confirm/prompt state for iframe-safe Revoke.
  const [confirmRevokeToken, setConfirmRevokeToken] = useState("");
  const revokeLink = (token) => setConfirmRevokeToken(token);

  const performRevoke = async () => {
    const token = confirmRevokeToken;
    setConfirmRevokeToken("");
    if (!token) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/field/crew-link/${token}`, {
        method: "DELETE", credentials: "include",
      });
      if (r.ok) {
        showToast("✓ Link revoked");
        await loadAll();
      } else {
        showToast(`Could not revoke (server ${r.status})`, "error");
      }
    } catch {
      showToast("Could not revoke — network error", "error");
    }
  };

  const projLabel = (pid) => {
    const p = projects.find(x => x.project_id === pid);
    return p ? `${p.address}${p.unit_number ? " #" + p.unit_number : ""}` : pid;
  };

  return (
    <div data-testid="field-launcher-tab" style={{ padding: "0 0.5rem" }}>
      {toast && (
        <div data-testid="fl-toast" style={{ position: "fixed", top: 80, right: 20, zIndex: 1000, background: toast.kind === "error" ? "#7f1d1d" : "#064e3b", color: "#fff", padding: "0.85rem 1.25rem", borderRadius: 8, border: `1px solid ${toast.kind === "error" ? "#ef4444" : "#10b981"}`, fontSize: "0.86rem", fontWeight: 700 }}>
          {toast.msg}
        </div>
      )}

      <div style={{ marginBottom: "1.5rem" }}>
        <h1 style={{ color: "#fff", fontSize: "1.65rem", fontWeight: 800, margin: "0 0 0.4rem", letterSpacing: "-0.01em" }}>
          🛠 Field Mode · CAD/CAM Launcher
        </h1>
        <p style={{ color: "#ADBED1", fontSize: "0.92rem", margin: 0, lineHeight: 1.55, maxWidth: 820 }}>
          One-button launch for the crew. Pulls specs (palette, compliance flags, sourced materials), tallies usage live, captures photos, and emits a PDF shift report when they wrap. Crew accesses it through a magic link — no Google login needed.
        </p>
      </div>

      {/* Project picker + launch */}
      <div style={{ background: "linear-gradient(135deg, rgba(0,220,120,0.06) 0%, rgba(0,255,255,0.06) 100%)", border: "1px solid rgba(0,220,120,0.30)", borderRadius: 12, padding: "1.25rem 1.4rem", marginBottom: "1.25rem" }}>
        <h3 style={{ color: "#00DC78", fontSize: "0.95rem", fontWeight: 800, margin: "0 0 0.85rem" }}>
          Step 1 · Pick a project
        </h3>
        <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)} data-testid="fl-project-picker" style={inputStyle}>
          <option value="" style={{ background: "#030B1A" }}>— Choose a project —</option>
          {projects.map(p => (
            <option key={p.project_id} value={p.project_id} style={{ background: "#030B1A" }}>
              {p.address}{p.unit_number ? ` #${p.unit_number}` : ""} — {p.client_name || "—"}
            </option>
          ))}
        </select>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.85rem", marginTop: "1.1rem" }}>
          <button onClick={launchSelf} disabled={!selectedProject} data-testid="fl-launch-self" style={{
            ...primaryBtn,
            opacity: selectedProject ? 1 : 0.5,
            cursor: selectedProject ? "pointer" : "not-allowed",
            padding: "1rem 1.25rem", fontSize: "0.95rem",
          }}>
            ⚡ Launch for Me
          </button>
          <button onClick={generateCrewLink} disabled={!selectedProject} data-testid="fl-generate-link" style={{
            background: "rgba(255,255,255,0.06)", color: "#fff",
            border: "1px solid rgba(255,255,255,0.20)",
            padding: "1rem 1.25rem", borderRadius: 8, fontSize: "0.95rem",
            fontWeight: 800,
            opacity: selectedProject ? 1 : 0.5,
            cursor: selectedProject ? "pointer" : "not-allowed",
          }}>
            🔗 Generate Crew Link
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.85rem", marginTop: "0.9rem" }}>
          <div>
            <Label>Crew Role (for the link)</Label>
            <select value={crewRole} onChange={e => setCrewRole(e.target.value)} data-testid="fl-crew-role" style={inputStyle}>
              <option value="field_crew" style={{ background: "#030B1A" }}>Field Crew (general)</option>
              <option value="cleaning" style={{ background: "#030B1A" }}>Cleaning Crew</option>
              <option value="maintenance" style={{ background: "#030B1A" }}>Maintenance</option>
              <option value="family" style={{ background: "#030B1A" }}>Family / Sisters</option>
            </select>
          </div>
          <div>
            <Label>Link Valid For (days)</Label>
            <select value={expiresIn} onChange={e => setExpiresIn(parseInt(e.target.value, 10))} data-testid="fl-expires" style={inputStyle}>
              <option value="1" style={{ background: "#030B1A" }}>1 day</option>
              <option value="3" style={{ background: "#030B1A" }}>3 days</option>
              <option value="7" style={{ background: "#030B1A" }}>1 week</option>
              <option value="14" style={{ background: "#030B1A" }}>2 weeks</option>
              <option value="30" style={{ background: "#030B1A" }}>30 days (max)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Active crew links */}
      <h3 style={sectionTitle}>🔗 Active Crew Links ({crewLinks.length})</h3>
      {loading ? (
        <p style={{ color: "#ADBED1", fontSize: "0.85rem" }}>Loading…</p>
      ) : crewLinks.length === 0 ? (
        <p style={{ color: "#7E92A8", fontSize: "0.84rem", padding: "1rem", textAlign: "center", border: "1px dashed rgba(255,255,255,0.10)", borderRadius: 8 }}>
          No active links yet. Generate one above to give your crew access.
        </p>
      ) : (
        <div style={{ marginBottom: "1.5rem" }}>
          {crewLinks.map(l => {
            const fullUrl = `${window.location.origin}/crew/${l.token}`;
            const expDate = l.expires_at ? new Date(l.expires_at).toLocaleDateString() : "";
            return (
              <div key={l.token} data-testid={`crew-link-${l.token.slice(0,8)}`} style={linkCard}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.6rem", flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <p style={{ color: "#fff", fontSize: "0.86rem", fontWeight: 800, margin: 0 }}>
                      {projLabel(l.project_id)}
                    </p>
                    <p style={{ color: "#5AC8D8", fontSize: "0.74rem", margin: "3px 0 0", fontWeight: 700 }}>
                      {l.crew_role.replace("_", " ").toUpperCase()} · Expires {expDate} · Used {l.uses || 0}×
                    </p>
                    <code style={{ display: "block", marginTop: 6, padding: "0.4rem 0.55rem", background: "rgba(0,0,0,0.30)", borderRadius: 4, color: "#9FE4BC", fontSize: "0.7rem", overflow: "auto", wordBreak: "break-all" }}>
                      {fullUrl}
                    </code>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => copyLink(l.token)} data-testid={`copy-${l.token.slice(0,8)}`} style={smallBtn}>📋 Copy</button>
                    <button onClick={() => revokeLink(l.token)} data-testid={`revoke-${l.token.slice(0,8)}`} style={{ ...smallBtn, color: "#fca5a5", borderColor: "rgba(252,165,165,0.30)" }}>✕ Revoke</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Active sessions */}
      <h3 style={sectionTitle}>📋 Active Sessions ({sessions.length})</h3>
      {sessions.length === 0 ? (
        <p style={{ color: "#7E92A8", fontSize: "0.84rem", padding: "1rem", textAlign: "center", border: "1px dashed rgba(255,255,255,0.10)", borderRadius: 8 }}>
          No crew currently on site.
        </p>
      ) : (
        <div>
          {sessions.map(s => (
            <div key={s.session_id} style={linkCard}>
              <p style={{ color: "#fff", fontSize: "0.86rem", fontWeight: 800, margin: 0 }}>{s.project_address}</p>
              <p style={{ color: "#00DC78", fontSize: "0.74rem", fontWeight: 700, margin: "3px 0 0" }}>
                🟢 {s.crew_name} · {s.room_type} · {(s.materials_tallied || []).length} items · {s.photos_count || 0} photos
              </p>
              <p style={{ color: "#7E92A8", fontSize: "0.7rem", margin: "3px 0 0" }}>
                Started {new Date(s.started_at).toLocaleString()}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Copy fallback modal — shown when clipboard write fails inside iframe */}
      {copyModalUrl && (
        <div data-testid="fl-copy-modal" role="dialog"
             onClick={() => setCopyModalUrl("")}
             style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", zIndex: 2000,
                      display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
          <div onClick={(e) => e.stopPropagation()}
               style={{ background: "#0A1930", border: "1px solid #00FFFF66", borderRadius: 10,
                        padding: "1.4rem 1.2rem", maxWidth: 520, width: "100%",
                        boxShadow: "0 0 40px rgba(0,255,255,0.35)" }}>
            <div style={{ color: "#00FFFF", fontFamily: "monospace", fontSize: "0.72rem", letterSpacing: "0.22em", fontWeight: 800, marginBottom: 10 }}>
              &gt; MANUAL_COPY_REQUIRED
            </div>
            <p style={{ color: "#fff", fontSize: "0.86rem", margin: "0 0 0.9rem", lineHeight: 1.5 }}>
              Clipboard is blocked inside this preview. Long-press the URL below to copy it, or open it in a new tab.
            </p>
            <input readOnly value={copyModalUrl} data-testid="fl-copy-modal-input"
                   onFocus={(e) => e.target.select()}
                   style={{ width: "100%", padding: "0.75rem 0.85rem", borderRadius: 6,
                            border: "1px solid #00FFFF88", background: "#020A1F",
                            color: "#fff", fontFamily: "monospace", fontSize: "0.82rem", boxSizing: "border-box" }} />
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <a href={copyModalUrl} target="_blank" rel="noreferrer" data-testid="fl-copy-modal-open"
                 style={{ flex: 1, background: "transparent", color: "#00FFFF", border: "1px solid #00FFFF",
                          borderRadius: 6, padding: "0.7rem", textAlign: "center", fontFamily: "monospace",
                          fontSize: "0.76rem", fontWeight: 800, letterSpacing: "0.1em", textDecoration: "none" }}>
                OPEN IN NEW TAB
              </a>
              <button type="button" data-testid="fl-copy-modal-close"
                      onClick={() => setCopyModalUrl("")}
                      style={{ flex: 1, background: "#00FFFF", color: "#020A1F", border: "none",
                               borderRadius: 6, padding: "0.7rem", fontFamily: "monospace",
                               fontSize: "0.76rem", fontWeight: 800, letterSpacing: "0.1em", cursor: "pointer" }}>
                CLOSE
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revoke confirm modal — iframe-safe replacement for window.confirm() */}
      {confirmRevokeToken && (
        <div data-testid="fl-revoke-modal" role="dialog"
             onClick={() => setConfirmRevokeToken("")}
             style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", zIndex: 2000,
                      display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
          <div onClick={(e) => e.stopPropagation()}
               style={{ background: "#0A1930", border: "1px solid #ef4444aa", borderRadius: 10,
                        padding: "1.4rem 1.2rem", maxWidth: 460, width: "100%",
                        boxShadow: "0 0 40px rgba(239,68,68,0.35)" }}>
            <div style={{ color: "#ef4444", fontFamily: "monospace", fontSize: "0.72rem", letterSpacing: "0.22em", fontWeight: 800, marginBottom: 10 }}>
              &gt; CONFIRM_REVOKE
            </div>
            <p style={{ color: "#fff", fontSize: "0.9rem", margin: "0 0 1rem", lineHeight: 1.5 }}>
              Revoke this crew link? Crew won&rsquo;t be able to join with it anymore.
            </p>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" data-testid="fl-revoke-cancel"
                      onClick={() => setConfirmRevokeToken("")}
                      style={{ flex: 1, background: "transparent", color: "#fff",
                               border: "1px solid rgba(255,255,255,0.25)", borderRadius: 6,
                               padding: "0.75rem", fontFamily: "monospace", fontSize: "0.78rem",
                               fontWeight: 700, letterSpacing: "0.1em", cursor: "pointer" }}>
                CANCEL
              </button>
              <button type="button" data-testid="fl-revoke-confirm"
                      onClick={performRevoke}
                      style={{ flex: 1, background: "#ef4444", color: "#fff", border: "none",
                               borderRadius: 6, padding: "0.75rem", fontFamily: "monospace",
                               fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.1em", cursor: "pointer" }}>
                REVOKE
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Label = ({ children }) => (
  <label style={{ display: "block", color: "#ADBED1", fontSize: "0.72rem", fontWeight: 700, marginBottom: "0.25rem", letterSpacing: "0.04em", textTransform: "uppercase" }}>
    {children}
  </label>
);

const inputStyle = {
  width: "100%", padding: "0.65rem 0.85rem", borderRadius: 6,
  border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.04)",
  color: "#fff", fontSize: "0.86rem", boxSizing: "border-box",
};
const primaryBtn = {
  background: "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)", color: "#030B1A",
  border: "none", borderRadius: 8, fontWeight: 800, cursor: "pointer",
};
const sectionTitle = { color: "#fff", fontSize: "1rem", fontWeight: 800, margin: "1.5rem 0 0.85rem" };
const linkCard = { padding: "0.85rem 1rem", background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, marginBottom: "0.6rem" };
const smallBtn = { background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,0.20)", padding: "0.35rem 0.65rem", borderRadius: 5, fontSize: "0.72rem", fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" };

export default FieldLauncherTab;
